import { concatBytes, copyBytes } from "./bytes.js";
import { dialOnce } from "./client.js";
import type { BlindConnection } from "./connection.js";
import type { Endpoint } from "./endpoint.js";
import { BlindBindError } from "./errors.js";
import { ByteStream } from "./stream.js";
import type { WebSocketFactory } from "./websocket.js";

const headerTerminator = new TextEncoder().encode("\r\n\r\n");
const lineTerminator = new TextEncoder().encode("\r\n");
const maximumHeaderBytes = 64 << 10;
const defaultMaximumResponseBytes = 64 << 20;

export type Route = { type: "direct"; origin: string } | { type: "relay"; endpoint: Endpoint };

export interface RouteRequestOptions {
  maximumResponseBytes?: number;
  handshakeTimeoutMs?: number;
  webSocketFactory?: WebSocketFactory;
}

export async function request(
  route: Route,
  path: string,
  init: RequestInit = {},
  options: RouteRequestOptions = {},
): Promise<Response> {
  const normalizedPath = normalizePath(path);
  if (route.type === "direct") return fetch(new URL(normalizedPath, normalizeOrigin(route.origin)), init);
  const maximumResponseBytes = options.maximumResponseBytes ?? defaultMaximumResponseBytes;
  if (!Number.isSafeInteger(maximumResponseBytes) || maximumResponseBytes < 1) {
    throw new Error("Maximum response size must be a positive integer");
  }
  const connection = await dialOnce({
    endpoint: route.endpoint,
    ...(init.signal ? { signal: init.signal } : {}),
    ...(options.handshakeTimeoutMs !== undefined ? { handshakeTimeoutMs: options.handshakeTimeoutMs } : {}),
    ...(options.webSocketFactory ? { webSocketFactory: options.webSocketFactory } : {}),
  });
  return requestOverConnection(connection, normalizedPath, init, maximumResponseBytes);
}

async function requestOverConnection(
  connection: BlindConnection,
  path: string,
  init: RequestInit,
  maximumResponseBytes: number,
): Promise<Response> {
  const abort = () => connection.close({ reason: "request aborted" });
  init.signal?.addEventListener("abort", abort, { once: true });
  const reader = new ByteStream(connection.readable);
  const writer = connection.writable.getWriter();
  try {
    const normalized = new Request(`http://blind.invalid${path}`, init);
    const body = normalized.body === null ? new Uint8Array(0) : new Uint8Array(await normalized.arrayBuffer());
    const headers = new Headers(normalized.headers);
    headers.set("Host", "blind.invalid");
    headers.set("Connection", "keep-alive");
    if (
      body.byteLength > 0 ||
      normalized.method === "POST" ||
      normalized.method === "PUT" ||
      normalized.method === "PATCH"
    ) {
      headers.set("Content-Length", String(body.byteLength));
    }
    headers.delete("Transfer-Encoding");
    const head = encodeRequestHead(normalized.method, path, headers);
    await writer.write(body.byteLength > 0 ? concatBytes(head, body) : head);
    const parsed = parseResponseHead(await reader.readUntil(headerTerminator, maximumHeaderBytes));
    const responseBody = await readResponseBody(reader, normalized.method, parsed, maximumResponseBytes);
    return new Response(responseBody === null ? null : copyBytes(responseBody).buffer, {
      status: parsed.status,
      statusText: parsed.statusText,
      headers: parsed.headers,
    });
  } finally {
    init.signal?.removeEventListener("abort", abort);
    reader.release();
    writer.releaseLock();
    connection.close();
  }
}

interface ParsedResponse {
  status: number;
  statusText: string;
  headers: Headers;
}

function encodeRequestHead(method: string, path: string, headers: Headers): Uint8Array {
  const lines = [`${method} ${path} HTTP/1.1`];
  headers.forEach((value, name) => lines.push(`${name}: ${value}`));
  return new TextEncoder().encode(`${lines.join("\r\n")}\r\n\r\n`);
}

function parseResponseHead(value: Uint8Array): ParsedResponse {
  const text = decodeProtocolText(value.subarray(0, value.byteLength - headerTerminator.byteLength));
  const lines = text.split("\r\n");
  const statusLine = lines.shift();
  const match = /^HTTP\/1\.[01] ([0-9]{3})(?: (.*))?$/.exec(statusLine ?? "");
  if (!match) throw new BlindBindError("protocol_error", "Peer returned an invalid HTTP status line");
  const status = Number(match[1]);
  if (status < 100 || status > 599) throw new BlindBindError("protocol_error", "Peer returned an invalid HTTP status");
  const headers = new Headers();
  for (const line of lines) {
    const separator = line.indexOf(":");
    if (separator < 1) throw new BlindBindError("protocol_error", "Peer returned an invalid HTTP header");
    headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim());
  }
  return { status, statusText: match[2] ?? "", headers };
}

async function readResponseBody(
  reader: ByteStream,
  method: string,
  response: ParsedResponse,
  maximumBytes: number,
): Promise<Uint8Array | null> {
  if (
    method === "HEAD" ||
    response.status === 204 ||
    response.status === 304 ||
    (response.status >= 100 && response.status < 200)
  ) {
    return null;
  }
  const transferEncoding = response.headers.get("Transfer-Encoding")?.toLowerCase();
  if (transferEncoding) {
    if (transferEncoding !== "chunked")
      throw new BlindBindError("protocol_error", "Peer used an unsupported transfer encoding");
    return readChunkedBody(reader, maximumBytes);
  }
  const rawLength = response.headers.get("Content-Length");
  if (rawLength !== null) {
    if (!/^[0-9]+$/.test(rawLength))
      throw new BlindBindError("protocol_error", "Peer returned an invalid content length");
    const length = Number(rawLength);
    if (!Number.isSafeInteger(length) || length > maximumBytes) {
      throw new BlindBindError("protocol_error", "Peer response exceeds the configured maximum");
    }
    return reader.readExactly(length);
  }
  if (headerContainsToken(response.headers, "Connection", "close")) return reader.readToEnd(maximumBytes);
  throw new BlindBindError("protocol_error", "Peer response has no HTTP body boundary");
}

async function readChunkedBody(reader: ByteStream, maximumBytes: number): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const line = decodeProtocolText(await reader.readUntil(lineTerminator, maximumHeaderBytes)).slice(0, -2);
    const sizeText = line.split(";", 1)[0]?.trim() ?? "";
    if (!/^[0-9a-fA-F]+$/.test(sizeText))
      throw new BlindBindError("protocol_error", "Peer returned an invalid chunk size");
    const length = Number.parseInt(sizeText, 16);
    if (length === 0) {
      for (;;) {
        const trailer = await reader.readUntil(lineTerminator, maximumHeaderBytes);
        if (trailer.byteLength === lineTerminator.byteLength) return concatMany(chunks, total);
      }
    }
    total += length;
    if (!Number.isSafeInteger(length) || total > maximumBytes) {
      throw new BlindBindError("protocol_error", "Peer response exceeds the configured maximum");
    }
    chunks.push(await reader.readExactly(length));
    const terminator = await reader.readExactly(2);
    if (terminator[0] !== 13 || terminator[1] !== 10) {
      throw new BlindBindError("protocol_error", "Peer returned an invalid chunk terminator");
    }
  }
}

function normalizeOrigin(value: string): string {
  const url = new URL(value);
  if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error("Direct route must use HTTP or HTTPS");
  if (url.username || url.password || url.search || url.hash || (url.pathname !== "" && url.pathname !== "/")) {
    throw new Error("Direct route must be an origin without credentials, query, fragment, or path");
  }
  return url.origin;
}

function normalizePath(value: string): string {
  const url = new URL(value, "http://blind.invalid");
  if (url.origin !== "http://blind.invalid" || url.username || url.password || url.hash) {
    throw new Error("Route path must be an absolute path without an origin, credentials, or fragment");
  }
  return `${url.pathname}${url.search}`;
}

function decodeProtocolText(value: Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(value);
  } catch (error) {
    throw new BlindBindError("protocol_error", "Peer returned invalid UTF-8 in an HTTP header", { cause: error });
  }
}

function headerContainsToken(headers: Headers, name: string, expected: string): boolean {
  return (headers.get(name) ?? "").split(",").some((value) => value.trim().toLowerCase() === expected.toLowerCase());
}

function concatMany(chunks: Uint8Array[], total: number): Uint8Array {
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(copyBytes(chunk), offset);
    offset += chunk.byteLength;
  }
  return result;
}
